/**
 * V5 Chat Dashboard
 * Migrated from V4ChatDashboard with demo_ui design patterns
 * Features: 3-panel layout (left: agents/metrics, center: chat, right: objectives), Axone logo, emerald theme
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Editor from '@monaco-editor/react';
import {
    Send, Target, Clock, AlertCircle,
    ChevronRight, ChevronLeft, Menu, ArrowLeft, CheckCircle2,
    MessageSquare, Loader2, RefreshCw, Upload, Mic, MicOff, Keyboard
} from 'lucide-react';
import clsx from 'clsx';
import useV5Chat, { ChatMessage, Agent } from '../hooks/useV5Chat';
import { useCodingTasks } from '../hooks/useCodingTasks';
import { useVoiceMode, getVoiceIdForAgent } from '../hooks/useVoiceMode';
import { Avatar } from './Avatar';
import { VoiceOrb } from './VoiceOrb';
import { useTour, TourStep } from '../hooks/useTour';
import { TourTooltip } from './TourTooltip';
import { EndSimulationModal } from './EndSimulationModal';
import { NavigationGuardModal } from './NavigationGuardModal';
import { TimeExpiredModal } from './TimeExpiredModal';
import { CodeEditor } from './CodeEditor';
import { FinalEvaluation } from '../../v4/components/FinalEvaluation';
import { useAuth } from '../../contexts/AuthContext';

interface V5ChatDashboardProps {
    sessionId: string;
    onBack?: () => void;
}

export function V5ChatDashboard({ sessionId, onBack }: V5ChatDashboardProps) {
    const navigate = useNavigate();
    
    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/app');
        }
    };
    
    // Tour steps definition - placements adjusted for element positions
    const tourSteps: TourStep[] = [
        {
            id: 'conversations',
            target: 'conversations',
            title: 'Conversations',
            content: 'Start conversations with your teammates to gather information, resolve conflicts, and complete objectives. Remember, every interaction counts! Click on any conversation to begin chatting.',
            placement: 'right',
        },
        {
            id: 'simulation-goal',
            target: 'simulation-goal',
            title: 'Simulation Goal',
            content: 'This is your primary objective for the simulation. Every interaction with an agent will contribute (or detract) to your progress towards achieving this goal.',
            placement: 'left',
        },
        {
            id: 'objectives',
            target: 'objectives',
            title: 'Objectives',
            content: 'Keep an eye on your objectives here. These can be affected by interacting with either a single person or multiple people at once. Complete these objectives to achieve your simulation goal.',
            placement: 'left',
        },
    ];

    const tour = useTour(tourSteps);
    const { user } = useAuth();

    const [message, setMessage] = useState('');
    const [showLeftPanel, setShowLeftPanel] = useState(true);
    const [showRightPanel, setShowRightPanel] = useState(true);
    const [showEndModal, setShowEndModal] = useState(false);
    const [showNavigationGuard, setShowNavigationGuard] = useState(false);
    const [showCodeEditor, setShowCodeEditor] = useState(false);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    
    // Track all messages that have been spoken to prevent repeats
    const spokenMessageIdsRef = useRef<Set<string>>(new Set());
    
    // Load previously spoken message IDs from session storage on mount
    useEffect(() => {
        const storageKey = `spoken_messages_${sessionId}`;
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
            try {
                const ids = JSON.parse(stored);
                spokenMessageIdsRef.current = new Set(ids);
            } catch (e) {
                // Ignore parse errors
            }
        }
    }, [sessionId]);
    
    // Save spoken message IDs to session storage when they change
    const markMessageAsSpoken = useCallback((messageId: string) => {
        spokenMessageIdsRef.current.add(messageId);
        const storageKey = `spoken_messages_${sessionId}`;
        sessionStorage.setItem(storageKey, JSON.stringify([...spokenMessageIdsRef.current]));
    }, [sessionId]);

    // Get user's display name for evaluation
    const userName = user?.fullName || user?.firstName || 'Candidate';

    // Use hook exactly like V4 (line 73, 86-92)
    const {
        simulationState,
        taskList,
        semanticProfile,
        isLoading,
        error,
        sendMessage: sendMsg,
        timeTracking,
        selectAgent,
        selectedAgent,
        selectedConversation,
        getAgentMessageCount,
        isAgentTyping,
        isAgentTypingInChat,
        generateFinalEvaluation,
        isGeneratingEvaluation,
        finalEvaluation,
        showTimeExpiredModal,
        setShowTimeExpiredModal,
        isNavigatingAway,
        cleanupSession,
        reportVoiceModeUsage,
        eventCodeMap,
        fetchEventCode,
        pushEventCode,
        hasUnreadMessages,
        setNewMessageCallback
    } = useV5Chat({ preloadedSessionId: sessionId });

    // Handle voice transcription - send as message to selected agent
    const handleVoiceTranscription = useCallback((text: string) => {
        if (selectedAgent && text.trim()) {
            sendMsg(selectedAgent.agent_id, text);
        }
    }, [selectedAgent, sendMsg]);

    // Handle voice errors
    const handleVoiceError = useCallback((error: string) => {
        setVoiceError(error);
        setTimeout(() => setVoiceError(null), 5000);
    }, []);

    // Voice mode hook
    const voiceMode = useVoiceMode({
        sessionId: sessionId,
        onTranscription: handleVoiceTranscription,
        onError: handleVoiceError,
    });

    // State for toast notifications
    const [toastNotification, setToastNotification] = useState<{
        agentName: string;
        messagePreview: string;
    } | null>(null);

    // Set up new message notification callback
    useEffect(() => {
        setNewMessageCallback((agentId: string, agentName: string, messagePreview: string) => {
            setToastNotification({ agentName, messagePreview });
            setTimeout(() => setToastNotification(null), 5000);
        });

        return () => {
            setNewMessageCallback(null);
        };
    }, [setNewMessageCallback]);

    // Coding tasks management (like V4 lines 99-104)
    const codingTasks = useCodingTasks(simulationState?.session_id || null);

    // Load coding tasks when simulation is active
    useEffect(() => {
        if (simulationState?.session_id) {
            codingTasks.loadTasks(simulationState.session_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulationState?.session_id]);

    // Extract data using V4's pattern (V4 lines 622, 1034, 89)
    const agents = simulationState?.agents || [];
    const messages = selectedConversation || [];
    const simulationGoal = taskList?.primary_goal?.description || '';
    const timeRemaining = timeTracking?.time_remaining_minutes || null;
    const simulationEnded = simulationState?.status === 'completed';
    const isCodingSimulation = simulationState?.metadata?.simulation_type === 'coding';

    const selectedAgentId = selectedAgent?.agent_id || null;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Auto-speak new agent messages when voice mode is enabled
    // Only speaks messages that haven't been spoken before (tracked in session storage)
    useEffect(() => {
        if (!voiceMode.isVoiceModeEnabled || messages.length === 0) return;
        if (voiceMode.voiceState === 'speaking' || voiceMode.voiceState === 'thinking') return;
        
        // Find the most recent unspoken agent message
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            const isAgentMessage = message.sender !== 'user';
            const hasBeenSpoken = spokenMessageIdsRef.current.has(message.message_id);
            
            if (isAgentMessage && !hasBeenSpoken) {
                // Mark as spoken immediately to prevent double-speaking
                markMessageAsSpoken(message.message_id);
                // Use a unique voice for each agent based on their ID
                const agentVoiceId = getVoiceIdForAgent(message.sender);
                console.log(`Speaking message from ${message.sender} with voice ${agentVoiceId}`);
                voiceMode.speakText(message.content, agentVoiceId);
                break; // Only speak one message at a time
            }
        }
    }, [messages, voiceMode.isVoiceModeEnabled, voiceMode.voiceState, voiceMode.speakText, markMessageAsSpoken]);

    // Block browser navigation during active simulation (like V4 lines 198-212)
    useEffect(() => {
        if (!simulationState) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [simulationState]);

    // Intercept browser back/forward button navigation
    useEffect(() => {
        if (!simulationState) return;

        // Push a dummy state to history so we can detect back button
        window.history.pushState({ simulationActive: true }, '');

        const handlePopState = (e: PopStateEvent) => {
            // User pressed back/forward button
            // Push state again to prevent actual navigation
            window.history.pushState({ simulationActive: true }, '');
            // Show the navigation guard modal
            setShowNavigationGuard(true);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [simulationState]);

    // Auto-complete session when time expires (notify backend)
    useEffect(() => {
        if (showTimeExpiredModal && simulationState?.session_id) {
            const completeExpiredSession = async () => {
                try {
                    const apiBase = import.meta.env.VITE_V4_API_BASE ||
                        (import.meta.env.VITE_API_BASE ? `${import.meta.env.VITE_API_BASE}/api/v4` : null) ||
                        'http://localhost:5000/api/v4';
                    
                    await fetch(`${apiBase}/sessions/${simulationState.session_id}/complete`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ reason: 'time_expired' })
                    });
                    console.log('Session marked as completed due to time expiry');
                } catch (error) {
                    console.error('Failed to mark session as completed:', error);
                }
            };
            completeExpiredSession();
        }
    }, [showTimeExpiredModal, simulationState?.session_id]);

    // Auto-redirect to evaluation after 2 minutes when time expired modal is shown
    useEffect(() => {
        if (showTimeExpiredModal && !finalEvaluation && !isGeneratingEvaluation) {
            const autoRedirectTimer = setTimeout(async () => {
                console.log('Auto-redirecting to evaluation after 2 minutes');
                try {
                    await generateFinalEvaluation(userName);
                } catch (err) {
                    console.error('Failed to auto-generate evaluation:', err);
                }
            }, 2 * 60 * 1000); // 2 minutes

            return () => clearTimeout(autoRedirectTimer);
        }
    }, [showTimeExpiredModal, finalEvaluation, isGeneratingEvaluation, generateFinalEvaluation, userName]);

    // Format time like V4 (H:MM:SS or M:SS)
    const formatTimeRemaining = (minutes: number): string => {
        const totalSeconds = Math.floor(minutes * 60);
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSendMessage = () => {
        if (!message.trim() || !selectedAgentId) return;
        sendMsg(selectedAgentId, message);
        setMessage('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Show final evaluation if available (like V4 line 490-492)
    // Also keep showing evaluation if navigating away to prevent flicker
    if (finalEvaluation || isNavigatingAway) {
        return (
            <FinalEvaluation 
                evaluation={finalEvaluation || {}} 
                onClose={() => {
                    // Navigate first, then cleanup in background to prevent flicker
                    handleBack();
                    const voiceUsed = voiceMode.usedSeconds > 0;
                    cleanupSession(voiceUsed, voiceMode.usedSeconds);
                }}
                candidateName={userName}
                simulationTitle={taskList?.primary_goal?.title || 'Simulation'}
            />
        );
    }

    return (
        <div className="h-screen flex flex-col bg-slate-50">
            {/* Toast Notification for New Messages */}
            <AnimatePresence>
                {toastNotification && (
                    <motion.div
                        initial={{ opacity: 0, y: -50, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: -50, x: '-50%' }}
                        className="fixed top-4 left-1/2 z-50 bg-white border border-slate-200 rounded-lg shadow-xl px-4 py-3 max-w-md"
                    >
                        <div className="flex items-start gap-3">
                            <Avatar name={toastNotification.agentName} size="md" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-900">
                                        New message from {toastNotification.agentName}
                                    </p>
                                    <button
                                        onClick={() => setToastNotification(null)}
                                        className="text-slate-400 hover:text-slate-600 ml-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                                    {toastNotification.messagePreview}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header with Axone Logo */}
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setShowNavigationGuard(true)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    <img
                        src="/axone_logo.png"
                        alt="Axone"
                        className="h-10 w-auto"
                    />
                    <div className="h-8 w-px bg-slate-200" />
                    <div>
                        <h1 className="text-lg font-bold text-slate-900">
                            {taskList?.primary_goal?.title || 'Loading...'}
                        </h1>
                        <p className="text-sm text-slate-600">
                            {taskList?.primary_goal?.description || ''}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {timeRemaining !== null && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <Clock className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-semibold text-emerald-700">
                                {formatTimeRemaining(timeRemaining)}
                            </span>
                        </div>
                    )}

                    <button
                        onClick={() => setShowEndModal(true)}
                        disabled={isGeneratingEvaluation}
                        className={clsx(
                            'px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors',
                            isGeneratingEvaluation
                                ? 'bg-red-400 cursor-not-allowed'
                                : 'bg-red-500 hover:bg-red-600'
                        )}
                    >
                        {isGeneratingEvaluation ? 'Generating...' : 'End Simulation'}
                    </button>

                    <button
                        onClick={() => setShowLeftPanel(!showLeftPanel)}
                        className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </header>

            {/* Main Content - 3 Panel Layout */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Panel - Agents & Metrics */}
                <AnimatePresence>
                    {showLeftPanel && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 320, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            className="bg-white border-r border-slate-200 flex flex-col overflow-hidden"
                        >
                            <div className="p-4 border-b border-slate-200" data-tour="conversations">
                                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                                    Conversations
                                </h2>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {agents.map(agent => (
                                    <button
                                        key={agent.agent_id}
                                        onClick={() => {
                                            selectAgent(agent.agent_id);
                                            setShowCodeEditor(false);
                                        }}
                                        className={clsx(
                                            'w-full p-3 rounded-lg text-left transition-all relative',
                                            selectedAgentId === agent.agent_id && !showCodeEditor
                                                ? 'bg-emerald-50 border-2 border-emerald-500'
                                                : 'bg-slate-50 border-2 border-transparent hover:border-slate-300',
                                            hasUnreadMessages(agent.agent_id) && selectedAgentId !== agent.agent_id && 'ring-2 ring-red-400 ring-offset-1'
                                        )}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="relative">
                                                <Avatar name={agent.name} size="xs" />
                                                {/* Unread indicator badge */}
                                                {hasUnreadMessages(agent.agent_id) && selectedAgentId !== agent.agent_id && (
                                                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={clsx(
                                                        'font-semibold text-sm truncate',
                                                        selectedAgentId === agent.agent_id && !showCodeEditor ? 'text-emerald-700' : 'text-slate-900',
                                                        hasUnreadMessages(agent.agent_id) && selectedAgentId !== agent.agent_id && 'text-red-600'
                                                    )}>
                                                        {agent.name}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        {/* Unread message badge */}
                                                        {hasUnreadMessages(agent.agent_id) && selectedAgentId !== agent.agent_id && (
                                                            <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                                                                Unread
                                                            </span>
                                                        )}
                                                        {isAgentTyping(agent.agent_id) && (
                                                            <div className="flex gap-1 ml-2">
                                                                {[0, 1, 2].map(i => (
                                                                    <motion.div
                                                                        key={i}
                                                                        className="w-1.5 h-1.5 bg-emerald-500 rounded-full"
                                                                        animate={{ y: [0, -4, 0] }}
                                                                        transition={{
                                                                            duration: 0.6,
                                                                            repeat: Infinity,
                                                                            delay: i * 0.2
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <p className="text-xs text-slate-600 truncate">{agent.role}</p>
                                                <div className="mt-1 text-xs text-slate-500">
                                                    {getAgentMessageCount(agent.agent_id)} messages
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Coding Tasks Section */}
                            {codingTasks.tasks.length > 0 && (
                                <>
                                    <div className="p-4 border-t border-slate-200">
                                        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                                            Coding Tasks
                                        </h2>
                                    </div>
                                    <div className="px-4 pb-4 space-y-2 max-h-60 overflow-y-auto">
                                        {codingTasks.tasks.map(task => (
                                            <button
                                                key={task.task_id}
                                                onClick={() => {
                                                    codingTasks.selectTask(task.task_id);
                                                    setShowCodeEditor(true);
                                                }}
                                                className={clsx(
                                                    'w-full p-3 rounded-lg text-left transition-all',
                                                    codingTasks.selectedTask?.task_id === task.task_id && showCodeEditor
                                                        ? 'bg-emerald-50 border-2 border-emerald-500'
                                                        : 'bg-slate-50 border-2 border-transparent hover:border-slate-300'
                                                )}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <span className="text-xs font-bold text-purple-600">
                                                            {task.language?.substring(0, 2).toUpperCase() || 'CO'}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className={clsx(
                                                            'font-semibold text-sm block truncate',
                                                            codingTasks.selectedTask?.task_id === task.task_id && showCodeEditor
                                                                ? 'text-emerald-700'
                                                                : 'text-slate-900'
                                                        )}>
                                                            {task.title}
                                                        </span>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-xs text-slate-500">
                                                                {task.language || 'Code task'}
                                                            </span>
                                                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                                                {task.task_type}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Center Panel - Chat or Code Editor */}
                <div className="flex-1 flex flex-col bg-white overflow-hidden">
                    {showCodeEditor && codingTasks.selectedTask ? (
                        // Code Editor View
                        <CodeEditor
                            task={codingTasks.selectedTask}
                            sessionId={simulationState?.session_id || ''}
                            onRun={async (code) => {
                                return await codingTasks.executeCode(
                                    codingTasks.selectedTask!.task_id,
                                    code,
                                    codingTasks.selectedTask!.language || 'python'
                                );
                            }}
                            onPush={async (code) => {
                                return await codingTasks.pushCode(
                                    codingTasks.selectedTask!.task_id,
                                    code,
                                    codingTasks.selectedTask!.language || 'python'
                                );
                            }}
                            isRunning={codingTasks.isExecuting}
                            isPushing={codingTasks.isPushing}
                            lastResult={codingTasks.lastExecutionResult || undefined}
                        />
                    ) : (
                        // Chat View
                        <>
                            {/* Chat Header */}
                            <div className="px-6 py-4 border-b border-slate-200">
                                {selectedAgent ? (
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">{selectedAgent.name}</h2>
                                        <p className="text-sm text-slate-600">{selectedAgent.role}</p>
                                    </div>
                                ) : (
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">Select an Agent or Task</h2>
                                        <p className="text-sm text-slate-600">Choose an agent to chat or a coding task from the left panel</p>
                                    </div>
                                )}
                            </div>

                            {/* Messages Area */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
                                {!selectedAgentId ? (
                                    <div className="h-full flex items-center justify-center">
                                        <div className="text-center max-w-md">
                                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <MessageSquare className="w-8 h-8 text-slate-400" />
                                            </div>
                                            <p className="text-slate-600">
                                                Select an agent from the left panel to begin your simulation
                                            </p>
                                        </div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="h-full flex items-center justify-center">
                                        <div className="text-center max-w-md">
                                            <p className="text-slate-600">No messages yet. Start the conversation!</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {messages.map((msg, idx) => (
                                            <MessageBubble
                                                key={msg.message_id || idx}
                                                message={msg}
                                                isUser={msg.sender === 'user'}
                                                agentName={agents.find(a => a.agent_id === msg.sender)?.name || msg.sender}
                                                eventCodeMap={eventCodeMap}
                                                fetchEventCode={fetchEventCode}
                                                pushEventCode={pushEventCode}
                                                simulationType={simulationState?.metadata?.simulation_type}
                                            />
                                        ))}

                                        {/* Typing indicator - only shows when agent is actively responding */}
                                        {selectedAgentId && isAgentTypingInChat(selectedAgentId) && (
                                            <div className="flex justify-start">
                                                <div className="bg-white rounded-2xl px-4 py-3 border border-slate-200 max-w-md shadow-sm">
                                                    <div className="flex gap-1.5">
                                                        {[0, 1, 2].map(i => (
                                                            <motion.div
                                                                key={i}
                                                                className="w-2 h-2 bg-emerald-500 rounded-full"
                                                                animate={{ 
                                                                    scale: [1, 1.3, 1],
                                                                    opacity: [0.5, 1, 0.5]
                                                                }}
                                                                transition={{
                                                                    duration: 1.2,
                                                                    repeat: Infinity,
                                                                    delay: i * 0.2,
                                                                    ease: 'easeInOut'
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </>
                                )}
                            </div>

                            {/* Message Input / Voice Mode */}
                            <div className="p-4 border-t border-slate-200 bg-white">
                                {/* Error Messages */}
                                {(error || voiceError) && (
                                    <div className="mb-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error || voiceError}
                                    </div>
                                )}

                                <AnimatePresence mode="wait">
                                    {voiceMode.isVoiceModeEnabled && selectedAgentId && !isCodingSimulation ? (
                                        /* Voice Mode Active - Show Voice Orb (hidden for coding simulations) */
                                        <motion.div
                                            key="voice-mode"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            className="flex flex-col items-center py-4"
                                        >
                                            <VoiceOrb
                                                state={voiceMode.voiceState}
                                                audioLevel={voiceMode.audioLevel}
                                                remainingSeconds={voiceMode.remainingSeconds}
                                                isTimeLimitReached={voiceMode.isTimeLimitReached}
                                                onStartListening={voiceMode.startListening}
                                                onStopListening={voiceMode.stopListening}
                                                onStopSpeaking={voiceMode.stopSpeaking}
                                            />
                                            
                                            {/* Switch to Text Mode Button */}
                                            <button
                                                onClick={voiceMode.toggleVoiceMode}
                                                className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                            >
                                                <Keyboard className="w-4 h-4" />
                                                Switch to text mode
                                            </button>
                                        </motion.div>
                                    ) : (
                                        /* Text Mode - Show Text Input */
                                        <motion.div
                                            key="text-mode"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            className="flex gap-3"
                                        >
                                            <textarea
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                placeholder={selectedAgent ? `Message ${selectedAgent.name}...` : "Select an agent first..."}
                                                disabled={!selectedAgentId || isLoading || simulationEnded}
                                                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                                                rows={2}
                                            />
                                            
                                            {/* Voice Mode Toggle Button (hidden for coding simulations) */}
                                            {!isCodingSimulation && (
                                                <button
                                                    onClick={voiceMode.toggleVoiceMode}
                                                    disabled={!selectedAgentId || simulationEnded || voiceMode.isTimeLimitReached}
                                                    className={clsx(
                                                        'px-4 py-3 rounded-lg transition-colors flex items-center justify-center',
                                                        'disabled:opacity-50 disabled:cursor-not-allowed',
                                                        voiceMode.isTimeLimitReached
                                                            ? 'bg-slate-200 text-slate-400'
                                                            : 'bg-slate-100 hover:bg-emerald-100 text-slate-600 hover:text-emerald-600'
                                                    )}
                                                    title={voiceMode.isTimeLimitReached ? 'Voice time limit reached' : 'Switch to voice mode'}
                                                >
                                                    {voiceMode.isTimeLimitReached ? (
                                                        <MicOff className="w-5 h-5" />
                                                    ) : (
                                                        <Mic className="w-5 h-5" />
                                                    )}
                                                </button>
                                            )}
                                            
                                            {/* Send Button */}
                                            <button
                                                onClick={handleSendMessage}
                                                disabled={!message.trim() || !selectedAgentId || isLoading || simulationEnded}
                                                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                {isLoading ? (
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <Send className="w-5 h-5" />
                                                )}
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Voice Time Indicator (shown in text mode when voice has been used, hidden for coding simulations) */}
                                {!isCodingSimulation && !voiceMode.isVoiceModeEnabled && voiceMode.usedSeconds > 0 && !voiceMode.isTimeLimitReached && (
                                    <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-500">
                                        <Mic className="w-3 h-3" />
                                        <span>
                                            {Math.floor(voiceMode.remainingSeconds / 60)}:{(voiceMode.remainingSeconds % 60).toString().padStart(2, '0')} voice time remaining
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Right Panel - Objectives with Edge Toggle */}
                <div className="relative flex">
                    {/* Toggle Button - Always Visible */}
                    <button
                        onClick={() => setShowRightPanel(!showRightPanel)}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-slate-700 hover:bg-slate-800 text-white rounded-lg shadow-lg flex items-center justify-center transition-all hover:scale-110"
                        aria-label={showRightPanel ? 'Hide objectives panel' : 'Show objectives panel'}
                    >
                        {showRightPanel ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <ChevronLeft className="w-5 h-5" />
                        )}
                    </button>

                    <AnimatePresence>
                        {showRightPanel && (
                            <motion.div
                                initial={{ width: 0, opacity: 0 }}
                                animate={{ width: 320, opacity: 1 }}
                                exit={{ width: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className="bg-white border-l border-slate-200 flex flex-col overflow-hidden"
                            >
                                <div className="p-4 border-b border-slate-200" data-tour="objectives">
                                    <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide flex items-center gap-2">
                                        <Target className="w-4 h-4 text-emerald-600" />
                                        Your Objectives
                                    </h2>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4">
                                    {/* Simulation Goal with Health & Progress */}
                                    {taskList?.primary_goal && (
                                        <div className="mb-6 p-4 bg-emerald-50 border-2 border-emerald-300 rounded-lg shadow-sm" data-tour="simulation-goal">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-bold text-emerald-900">Simulation Goal</p>
                                                {semanticProfile?.simulation_health?.status && (
                                                    <span className={clsx(
                                                        'px-2 py-0.5 text-xs font-semibold rounded-full uppercase',
                                                        semanticProfile.simulation_health.status === 'improving' && 'bg-green-100 text-green-700',
                                                        semanticProfile.simulation_health.status === 'degrading' && 'bg-red-100 text-red-700',
                                                        semanticProfile.simulation_health.status === 'stable' && 'bg-yellow-100 text-yellow-700'
                                                    )}>
                                                        {semanticProfile.simulation_health.status}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-emerald-800 mb-3">{taskList.primary_goal.description}</p>
                                            
                                            {/* Health Score */}
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-medium text-emerald-700">Health Score</span>
                                                <span className={clsx(
                                                    'px-2 py-0.5 text-xs font-bold rounded',
                                                    (taskList.primary_goal.health_score || 0) >= 8 && 'bg-green-100 text-green-700',
                                                    (taskList.primary_goal.health_score || 0) >= 6 && (taskList.primary_goal.health_score || 0) < 8 && 'bg-yellow-100 text-yellow-700',
                                                    (taskList.primary_goal.health_score || 0) >= 4 && (taskList.primary_goal.health_score || 0) < 6 && 'bg-orange-100 text-orange-700',
                                                    (taskList.primary_goal.health_score || 0) < 4 && 'bg-red-100 text-red-700'
                                                )}>
                                                    {(taskList.primary_goal.health_score || 0).toFixed(1)}/10
                                                </span>
                                            </div>
                                            
                                            {/* Progress Bar */}
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium text-emerald-700">Progress</span>
                                                    <span className="text-xs font-semibold text-emerald-800">
                                                        {((taskList.overall_progress || 0) * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
                                                    <motion.div
                                                        className="h-full bg-emerald-500 rounded-full"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${(taskList.overall_progress || 0) * 100}%` }}
                                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {(taskList?.objectives || []).filter((objective: any) => objective.objective_type !== 'primary_goal').map((objective: any, idx: number) => {
                                            const healthScore = objective.health_score || 0;
                                            const progress = (objective.current_progress || 0) * 100;
                                            const getHealthColor = (score: number) => {
                                                if (score >= 8) return 'green';
                                                if (score >= 6) return 'yellow';
                                                if (score >= 4) return 'orange';
                                                return 'red';
                                            };
                                            const healthColor = getHealthColor(healthScore);
                                            
                                            return (
                                                <div
                                                    key={objective.objective_id || idx}
                                                    className={clsx(
                                                        'p-3 rounded-lg border-2 transition-all',
                                                        objective.status === 'completed'
                                                            ? 'bg-green-50 border-green-500'
                                                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                                                    )}
                                                >
                                                    <div className="flex items-start gap-2 mb-2">
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            {objective.status === 'completed' ? (
                                                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                            ) : (
                                                                <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                                                            )}
                                                        </div>
                                                        <p className={clsx(
                                                            'text-sm flex-1 font-medium',
                                                            objective.status === 'completed' ? 'text-green-900 line-through' : 'text-slate-700'
                                                        )}>
                                                            {typeof objective.title === 'string'
                                                                ? objective.title
                                                                : (objective.title?.text || objective.title?.value || objective.title?.name || JSON.stringify(objective.title) || 'Untitled')}
                                                        </p>
                                                    </div>
                                                    
                                                    {/* Health and Progress Indicators */}
                                                    <div className="ml-7 space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className={clsx(
                                                                'px-1.5 py-0.5 text-xs font-semibold rounded',
                                                                healthColor === 'green' && 'bg-green-100 text-green-700',
                                                                healthColor === 'yellow' && 'bg-yellow-100 text-yellow-700',
                                                                healthColor === 'orange' && 'bg-orange-100 text-orange-700',
                                                                healthColor === 'red' && 'bg-red-100 text-red-700'
                                                            )}>
                                                                Health: {healthScore.toFixed(1)}
                                                            </span>
                                                            <span className="text-xs font-medium text-slate-600">
                                                                {progress.toFixed(0)}%
                                                            </span>
                                                        </div>
                                                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                            <motion.div
                                                                className={clsx(
                                                                    'h-full rounded-full',
                                                                    healthColor === 'green' && 'bg-green-500',
                                                                    healthColor === 'yellow' && 'bg-yellow-500',
                                                                    healthColor === 'orange' && 'bg-orange-500',
                                                                    healthColor === 'red' && 'bg-red-500'
                                                                )}
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${progress}%` }}
                                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Tour Tooltip */}
            {tour.isActive && tour.currentTourStep && (
                <TourTooltip
                    step={tour.currentTourStep}
                    currentStepIndex={tour.currentStep}
                    totalSteps={tourSteps.length}
                    onNext={tour.nextStep}
                    onPrevious={tour.previousStep}
                    onSkip={tour.skipTour}
                />
            )}

            {/* End Simulation Confirmation Modal */}
            <EndSimulationModal
                isOpen={showEndModal}
                isLoading={isGeneratingEvaluation}
                onCancel={() => setShowEndModal(false)}
                onConfirm={async () => {
                    try {
                        // Report voice mode usage before generating evaluation
                        const voiceUsed = voiceMode.usedSeconds > 0;
                        await reportVoiceModeUsage(voiceUsed, voiceMode.usedSeconds);
                        await generateFinalEvaluation(userName);
                        setShowEndModal(false);
                    } catch (err) {
                        console.error('Failed to generate evaluation:', err);
                    }
                }}
            />

            {/* Navigation Guard Modal - Back Button Protection */}
            <NavigationGuardModal
                isOpen={showNavigationGuard}
                onStay={() => setShowNavigationGuard(false)}
                onLeave={async () => {
                    const voiceUsed = voiceMode.usedSeconds > 0;
                    await cleanupSession(voiceUsed, voiceMode.usedSeconds);
                    handleBack();
                }}
            />

            {/* Time Expired Modal - Auto-shows when timer runs out, stays until evaluation is ready */}
            {showTimeExpiredModal && timeTracking && timeTracking.has_time_limit && !finalEvaluation && (
                <TimeExpiredModal
                    elapsedMinutes={timeTracking.elapsed_minutes}
                    timeLimitMinutes={timeTracking.time_limit_minutes}
                    isLoading={isGeneratingEvaluation}
                    onViewEvaluation={async () => {
                        try {
                            // Report voice mode usage before generating evaluation
                            const voiceUsed = voiceMode.usedSeconds > 0;
                            await reportVoiceModeUsage(voiceUsed, voiceMode.usedSeconds);
                            await generateFinalEvaluation(userName);
                        } catch (err) {
                            console.error('Failed to generate evaluation:', err);
                        }
                    }}
                />
            )}
        </div>
    );
}

// Message Bubble Component
interface MessageBubbleProps {
    message: ChatMessage;
    isUser: boolean;
    agentName: string;
    eventCodeMap?: Record<string, any>;
    fetchEventCode?: (eventId: string) => Promise<void>;
    pushEventCode?: (eventId: string, code: string, filename: string, language: string) => Promise<void>;
    simulationType?: string;
}

function MessageBubble({ 
    message, 
    isUser, 
    agentName,
    eventCodeMap,
    fetchEventCode,
    pushEventCode,
    simulationType
}: MessageBubbleProps) {
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);
    const [editedCode, setEditedCode] = useState<string>('');

    const metadata = message.metadata || {};
    const isDynamicEvent = metadata.type === 'dynamic_event';
    const eventId = metadata.event_id;
    const eventType = metadata.event_type;

    const codingEventTypes = ['debug_request', 'code_review', 'performance_issue', 'feature_request', 'bug_fix'];
    const isCodingEvent = isDynamicEvent && eventType && codingEventTypes.includes(eventType);
    const showCodeButton = isCodingEvent && simulationType === 'coding' && eventCodeMap && fetchEventCode && pushEventCode;

    const eventCodeState = eventId && eventCodeMap ? eventCodeMap[eventId] : undefined;
    const hasCode = eventCodeState?.code;
    const isLoadingCode = eventCodeState?.isLoading;
    const isPushingCode = eventCodeState?.isPushing;
    const codeError = eventCodeState?.error;

    const handleFetchCode = async () => {
        if (eventId && fetchEventCode) {
            await fetchEventCode(eventId);
        }
    };

    const handlePushCode = async () => {
        if (eventId && pushEventCode && hasCode) {
            const currentFile = hasCode.files[selectedFileIndex];
            const code = editedCode || currentFile.content;
            await pushEventCode(eventId, code, currentFile.filename, hasCode.language);
        }
    };

    const getLanguageForMonaco = (language: string): string => {
        const languageMap: Record<string, string> = {
            'python': 'python',
            'javascript': 'javascript',
            'typescript': 'typescript',
            'java': 'java',
            'cpp': 'cpp',
            'c': 'c',
            'go': 'go',
            'rust': 'rust',
            'yaml': 'yaml',
            'json': 'json',
            'plaintext': 'plaintext',
        };
        return languageMap[language?.toLowerCase()] || 'plaintext';
    };

    const getCodeOutputTypeBadge = (codeOutputType: string) => {
        const colorMap: Record<string, { bg: string; text: string }> = {
            'debug': { bg: 'bg-red-100', text: 'text-red-700' },
            'review': { bg: 'bg-orange-100', text: 'text-orange-700' },
            'changes': { bg: 'bg-blue-100', text: 'text-blue-700' },
        };
        const colors = colorMap[codeOutputType] || { bg: 'bg-slate-100', text: 'text-slate-700' };
        return (
            <span className={clsx('px-2 py-0.5 text-xs font-semibold rounded capitalize', colors.bg, colors.text)}>
                {codeOutputType}
            </span>
        );
    };

    useEffect(() => {
        if (hasCode && hasCode.files && hasCode.files[selectedFileIndex]) {
            setEditedCode(hasCode.files[selectedFileIndex].content);
        }
    }, [hasCode, selectedFileIndex]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx('flex', isUser ? 'justify-end' : 'justify-start')}
        >
            <div className={clsx(
                'rounded-2xl px-4 py-3',
                hasCode ? 'max-w-[90%]' : 'max-w-[70%]',
                isUser
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white border border-slate-200 text-slate-900'
            )}>
                {!isUser && (
                    <p className="text-xs font-semibold mb-1 text-slate-600">{agentName}</p>
                )}
                
                {/* Markdown Content */}
                <div className={clsx(
                    'text-sm prose prose-sm max-w-none',
                    isUser ? 'prose-invert' : ''
                )}
                style={{
                    // Custom styles for markdown elements
                    ['--tw-prose-body' as string]: isUser ? 'inherit' : 'inherit',
                    ['--tw-prose-headings' as string]: isUser ? 'inherit' : 'inherit',
                }}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            code: ({ className, children, ...props }) => {
                                const isInline = !className;
                                return isInline ? (
                                    <code className={clsx(
                                        'px-1.5 py-0.5 rounded text-sm font-mono',
                                        isUser ? 'bg-emerald-600' : 'bg-slate-100 text-slate-800'
                                    )} {...props}>
                                        {children}
                                    </code>
                                ) : (
                                    <code className={clsx(
                                        'block p-3 rounded-lg text-sm font-mono overflow-x-auto my-2',
                                        isUser ? 'bg-emerald-600' : 'bg-slate-100 text-slate-800'
                                    )} {...props}>
                                        {children}
                                    </code>
                                );
                            },
                            pre: ({ children }) => (
                                <pre className={clsx(
                                    'rounded-lg overflow-x-auto my-2',
                                    isUser ? 'bg-emerald-600' : 'bg-slate-100'
                                )}>
                                    {children}
                                </pre>
                            ),
                            h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
                            blockquote: ({ children }) => (
                                <blockquote className={clsx(
                                    'border-l-4 pl-3 my-2 italic',
                                    isUser ? 'border-emerald-300' : 'border-slate-300'
                                )}>
                                    {children}
                                </blockquote>
                            ),
                            table: ({ children }) => (
                                <div className="overflow-x-auto my-2">
                                    <table className="min-w-full border-collapse text-sm">{children}</table>
                                </div>
                            ),
                            th: ({ children }) => (
                                <th className={clsx(
                                    'border px-2 py-1 text-left font-semibold',
                                    isUser ? 'border-emerald-400 bg-emerald-600' : 'border-slate-300 bg-slate-100'
                                )}>
                                    {children}
                                </th>
                            ),
                            td: ({ children }) => (
                                <td className={clsx(
                                    'border px-2 py-1',
                                    isUser ? 'border-emerald-400' : 'border-slate-300'
                                )}>
                                    {children}
                                </td>
                            ),
                        }}
                    >
                        {message.content}
                    </ReactMarkdown>
                </div>

                {/* Fetch Code Button */}
                {showCodeButton && !hasCode && !isLoadingCode && (
                    <div className="mt-3">
                        <button
                            onClick={handleFetchCode}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Fetch Code
                        </button>
                    </div>
                )}

                {/* Loading Code */}
                {isLoadingCode && (
                    <div className="mt-3 flex items-center gap-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-xs">Pulling latest code...</span>
                    </div>
                )}

                {/* Code Error */}
                {codeError && (
                    <div className="mt-3">
                        <p className="text-xs text-red-500">Error: {codeError}</p>
                    </div>
                )}

                {/* Inline Code Editor */}
                {hasCode && (
                    <div className="mt-3 border border-slate-300 rounded-lg overflow-hidden bg-slate-800">
                        {/* Code Header */}
                        <div className="bg-slate-700 px-3 py-2 border-b border-slate-600">
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    {getCodeOutputTypeBadge(hasCode.code_output_type)}
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                        {hasCode.language}
                                    </span>
                                </div>
                                {hasCode.files.length > 1 && (
                                    <div className="flex gap-1 overflow-x-auto">
                                        {hasCode.files.map((file: any, idx: number) => (
                                            <button
                                                key={idx}
                                                onClick={() => setSelectedFileIndex(idx)}
                                                className={clsx(
                                                    'px-2 py-1 text-xs rounded transition-colors',
                                                    selectedFileIndex === idx
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                                                )}
                                            >
                                                {file.filename}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {hasCode.files[selectedFileIndex] && (
                                    <p className="text-xs text-slate-300">
                                        {hasCode.files[selectedFileIndex].description}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Monaco Editor */}
                        <div className="h-[300px]">
                            <Editor
                                height="100%"
                                language={getLanguageForMonaco(hasCode.language)}
                                value={editedCode}
                                onChange={(value) => setEditedCode(value || '')}
                                theme="vs-dark"
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 12,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    tabSize: 2,
                                    wordWrap: 'on',
                                    readOnly: false,
                                }}
                            />
                        </div>

                        {/* Push Button */}
                        <div className="bg-slate-700 px-3 py-2 border-t border-slate-600 flex items-center justify-between">
                            <span className="text-xs text-slate-300">
                                {hasCode.files[selectedFileIndex]?.filename}
                            </span>
                            <button
                                onClick={handlePushCode}
                                disabled={isPushingCode}
                                className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isPushingCode ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Upload className="w-4 h-4" />
                                )}
                                {isPushingCode ? 'Submitting...' : 'Push for Review'}
                            </button>
                        </div>
                    </div>
                )}

                <p className={clsx(
                    'text-xs mt-1',
                    isUser ? 'text-emerald-100' : 'text-slate-500'
                )}>
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
            </div>
        </motion.div>
    );
}

export default V5ChatDashboard;
